import {
  CUSTOMER_HOME_PATH,
  ROLES,
  isServiceProviderPortalHost,
} from "../helpers/constants";
import {
  clearStoredLoginRedirect,
  encryptPayload,
  markLoginSessionFresh,
  navigateToCustomerDomain,
  navigateToDomain,
  rememberLoginPortal,
  resolveInAppPath,
} from "../helpers/utils";
import { CAR_ADMIN_HOME, isCarPartnerSession } from "../admin/countyRecorderAccess";

/** Mirrors config/unified_auth.php portal role groupings. */
const PORTAL_ROLE_GROUPS = {
  admin: [
    "Super Admin",
    "Admin",
    "Sub Admin",
    "Provider",
    "County Admin",
    "Affiliate Admin",
  ],
  service_provider: [
    "Service Provider",
    "Title Company",
    "Escrow Company",
    "Lender Service Provider",
    "Real Estate Agent",
    "Notary",
    "Attorney",
    "Other Service Provider",
  ],
  broker: [
    "Agent",
    "Broker Admin",
    "Sales Manager",
    "Representative",
    "Assistant",
    "Sub Admins",
    "Agent Sub Admin",
  ],
  customer: [
    "Customer",
    "Partner",
    "Professional",
    "Lender",
    "Broker",
    "Other",
    "Sub User",
  ],
};

const BROKER_ROLE_LABELS = PORTAL_ROLE_GROUPS.broker;

export function extractLoginRoles(data = {}) {
  if (Array.isArray(data.roles) && data.roles.length) {
    return [...new Set(data.roles.filter(Boolean))];
  }

  const role = data.role ?? data.user?.user_type;
  if (Array.isArray(role)) {
    return [...new Set(role.filter(Boolean))];
  }

  if (role) {
    return [role];
  }

  return [];
}

export function shouldShowRolePicker(data = {}) {
  return extractLoginRoles(data).length > 1;
}

/** County Admin accounts the EP admin marked for a text-message code. */
export function countyAdminLoginNeedsMfa(data = {}) {
  return Boolean(data?.sms_mfa_required || data?.mfa_enrollment_required);
}

/** Screen to show after a County Admin password is accepted. */
export function countyAdminAuthStep(data = {}) {
  if (data?.mfa_enrollment_required) {
    return "enroll-phone";
  }
  if (data?.sms_mfa_required) {
    return "sms-code";
  }
  return "choose";
}

/** True when login step 1 succeeded but MFA is required before tokens are issued. */
export function loginRequiresAuthenticationStep(data = {}) {
  if (data?.AccessToken) {
    return false;
  }

  const roles = extractLoginRoles(data);
  if (!roles.length) {
    return true;
  }

  return roles.some((role) => {
    const portal = portalForRole(role);
    return portal === "customer" || portal === "service_provider";
  });
}

/** True when the API returned tokens and roles — navigate without MFA. */
export function loginCanNavigateImmediately(data = {}) {
  return Boolean(data?.AccessToken) && extractLoginRoles(data).length > 0;
}

export function portalForRole(roleLabel) {
  if (!roleLabel) {
    return "customer";
  }

  for (const [portal, roles] of Object.entries(PORTAL_ROLE_GROUPS)) {
    if (roles.includes(roleLabel)) {
      return portal;
    }
  }

  return "customer";
}

export { isServiceProviderPortalHost };

/** Stay in the servicer app when already there; otherwise hand the session across. */
export function openServiceProviderPath(navigate, path) {
  const normalized = String(path || "").replace(/^\//, "");
  if (isServiceProviderPortalHost()) {
    navigate(`/${normalized}`, { replace: true });
    return;
  }
  navigateToDomain(normalized);
}

/** Ensure token + authorised are in localStorage before route guards run. */
export function persistLoginSession(response, encrypt = encryptPayload) {
  if (!response?.success) {
    return;
  }

  const data = response?.data ?? {};
  const accessToken = data?.AccessToken;

  if (!accessToken) {
    return;
  }

  localStorage.setItem("authToken", accessToken);
  localStorage.setItem("authorised", "true");
  markLoginSessionFresh();
  rememberLoginPortal();
  persistLoginRoles(data, encrypt);
}

export function persistLoginRoles(data = {}, encrypt = encryptPayload) {
  const roles = extractLoginRoles(data);

  if (!roles.length) {
    return;
  }

  localStorage.setItem("userRoles", JSON.stringify(roles));
  if (data?.admin_partner_scope === "car") {
    localStorage.setItem("adminPartnerScope", "car");
  } else if (roles.includes("County Recorder")) {
    localStorage.removeItem("adminPartnerScope");
  }

  if (data.default_portal) {
    localStorage.setItem("defaultPortal", data.default_portal);
  }

  if (roles.length === 1) {
    applySelectedRole(roles[0], roles, encrypt);
  }
}

export function applySelectedRole(roleLabel, allRoles = [], encrypt = encryptPayload) {
  localStorage.setItem("activeRole", roleLabel);
  localStorage.setItem("roles", encrypt(roleLabel));

  if (allRoles?.length) {
    localStorage.setItem("userRoles", JSON.stringify(allRoles));
  }
}

export function isLoginRedirectPathAllowedForRole(roleLabel, path) {
  const safePath = resolveInAppPath(path);
  if (!safePath || !roleLabel) {
    return false;
  }

  const normalized = safePath.toLowerCase();
  const isAdminStaffPath =
    normalized.startsWith("/admin-dashboard") ||
    normalized.startsWith("/admin/") ||
    normalized.startsWith("/admins") ||
    normalized.startsWith("/admin-users") ||
    normalized.startsWith("/customer-service");
  const isCustomerPath =
    normalized.startsWith("/properties") ||
    normalized.startsWith("/user-profile") ||
    normalized.startsWith("/subscriptions") ||
    normalized.startsWith("/scans") ||
    normalized.startsWith("/documents") ||
    normalized.startsWith("/settings") ||
    normalized.startsWith("/communication") ||
    normalized.startsWith("/sub-user") ||
    normalized.startsWith("/requested-documents") ||
    normalized.startsWith("/logs") ||
    normalized.startsWith("/order-sign") ||
    normalized.startsWith("/professionals");
  const isProfessionalPath = normalized.startsWith("/professional");
  const isBrokerPath =
    normalized.includes("/broker/") ||
    normalized.includes("/title/") ||
    normalized.includes("/insurance/") ||
    normalized.includes("/attorney/");
  const isServicerPath = normalized.startsWith("/servicer");
  // Mid-flow create screens should not be restored after login.
  const isServicerCreateFlow =
    normalized.startsWith("/clients/create") ||
    normalized === "/add-protection" ||
    normalized.startsWith("/create-account");
  const isServicerPortalPath =
    isServicerPath ||
    normalized === "/clients" ||
    normalized.startsWith("/clients/") ||
    normalized.startsWith("/documents") ||
    normalized.startsWith("/commissions") ||
    normalized.startsWith("/fulfillment-assignments") ||
    normalized.startsWith("/fulfillment/clients") ||
    normalized.startsWith("/closing-assignments") ||
    normalized.startsWith("/risk-assessments") ||
    normalized.startsWith("/search-property") ||
    normalized.startsWith("/request-document") ||
    normalized.startsWith("/jesse-protected");

  if (roleLabel === ROLES.admin || roleLabel === ROLES.subAdmin) {
    return isAdminStaffPath;
  }

  if (roleLabel === ROLES.countyRecorder) {
    return (
      (normalized.startsWith("/admin-dashboard") && !isCarPartnerSession()) ||
      normalized.startsWith("/admin-users") ||
      normalized.startsWith("/admin/")
    );
  }

  if (roleLabel === ROLES.provider || roleLabel === ROLES.AffiliateManager) {
    return (
      normalized.startsWith("/admin-users") || normalized.startsWith("/admin/")
    );
  }

  if (roleLabel === ROLES.professional || roleLabel === "Professional") {
    return isProfessionalPath;
  }

  if (BROKER_ROLE_LABELS.includes(roleLabel)) {
    return isBrokerPath;
  }

  if (portalForRole(roleLabel) === "service_provider") {
    if (isServicerCreateFlow) {
      return false;
    }
    return isServicerPortalPath;
  }

  if (isAdminStaffPath || isProfessionalPath || isBrokerPath || isServicerPath) {
    return false;
  }

  return isCustomerPath;
}

export function consumeRestorableLoginRedirect(roleLabel, previousPath, uID) {
  const safePreviousPath = resolveInAppPath(previousPath);
  const storedUserId = localStorage.getItem("previousUserID");

  if (!safePreviousPath || !uID || !storedUserId) {
    return null;
  }

  if (parseInt(uID, 10) !== parseInt(storedUserId, 10)) {
    clearStoredLoginRedirect();
    return null;
  }

  if (!isLoginRedirectPathAllowedForRole(roleLabel, safePreviousPath)) {
    clearStoredLoginRedirect();
    return null;
  }

  clearStoredLoginRedirect();
  return safePreviousPath;
}

export function navigateForRole(navigate, roleLabel, options = {}) {
  const {
    code,
    previousPath,
    uID,
    replace = true,
    portal,
  } = options;

  const targetPortal = portal || portalForRole(roleLabel);
  const onServicerHost = isServiceProviderPortalHost();

  if (targetPortal === "service_provider" && !onServicerHost) {
    navigateToDomain("clients");
    return;
  }

  if (targetPortal === "customer" && onServicerHost) {
    navigateToCustomerDomain("properties");
    return;
  }

  const navOptions = { replace };

  const restorablePath = consumeRestorableLoginRedirect(
    roleLabel,
    previousPath,
    uID,
  );

  if (restorablePath) {
    navigate(restorablePath, navOptions);
    return;
  }

  if (roleLabel === ROLES.admin || roleLabel === ROLES.subAdmin) {
    navigate("/admin-dashboard", navOptions);
    return;
  }

  if (roleLabel === ROLES.countyRecorder) {
    navigate(
      isCarPartnerSession() ? CAR_ADMIN_HOME : "/admin-dashboard",
      navOptions,
    );
    return;
  }

  if (roleLabel === ROLES.provider || roleLabel === ROLES.AffiliateManager) {
    navigate("/admin-users", navOptions);
    return;
  }

  if (roleLabel === ROLES.professional || roleLabel === "Professional") {
    localStorage.setItem("paymentPopup", 0);
    navigate("/professional-dashboard", navOptions);
    return;
  }

  if (BROKER_ROLE_LABELS.includes(roleLabel)) {
    navigate("/broker", navOptions);
    return;
  }

  if (targetPortal === "service_provider") {
    // Clients list — not /clients/create (that is for signing up a customer).
    navigate("/clients", navOptions);
    return;
  }

  localStorage.setItem("paymentPopup", 0);

  if (localStorage.getItem("plan_renewal")) {
    navigate("/subscriptions", navOptions);
    return;
  }

  if (localStorage.getItem("code") === "req_doc_email") {
    navigate("/requested-documents", navOptions);
    localStorage.removeItem("code");
    return;
  }

  if (code === "ios" || code === "android") {
    navigate(`${CUSTOMER_HOME_PATH}?from=${code}`, navOptions);
    return;
  }

  navigate(CUSTOMER_HOME_PATH, navOptions);
}

/**
 * After a successful login, either show the role picker or navigate immediately.
 * Returns { needsRolePicker, roles, defaultPortal } when the user must choose.
 */
export function tryLoginNavigation(navigate, loginResponse, options = {}) {
  const data = loginResponse?.data ?? loginResponse ?? {};
  const roles = extractLoginRoles(data);
  const encrypt = options.encryptPayload || encryptPayload;

  persistLoginSession(loginResponse, encrypt);

  if (roles.length > 1) {
    return {
      needsRolePicker: true,
      roles,
      defaultPortal: data.default_portal,
    };
  }

  const role = roles[0];
  if (role) {
    navigateForRole(navigate, role, options);
  }

  return { needsRolePicker: false };
}

export function completeRoleSelection(
  navigate,
  roleLabel,
  { roles = [], encryptPayload: encrypt = encryptPayload, ...options } = {},
) {
  applySelectedRole(roleLabel, roles, encrypt);
  navigateForRole(navigate, roleLabel, options);
}
